import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

// ── Global chill audio engine ───────────────────────────────────────────────
// Owns the single music <audio>, the layerable ambient <audio> loops and a
// WebAudio noise generator. Mounted once at the app root so playback survives
// closing the Chill window / navigating away. The Chill page is just a remote.
//
// Phase 2 is personal-only; state persists in localStorage. Phase 3 will layer a
// synced "room" on top (music station/track broadcast via the server) — volume,
// ambient layers and the background scene always stay personal.

const ChillCtx = createContext(null)
export function useChill() {
  return useContext(ChillCtx)
}

const LS_KEY = "chill_state"
const AUDIO_BASE = "/api/chill/audio/?path="

function stationUrl(station) {
  if (!station) return null
  if (station.stream) return station.stream
  if (station.file) return AUDIO_BASE + encodeURIComponent(station.file)
  return null
}

function loadState() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY)) || {}
  } catch {
    return {}
  }
}

// Best-effort display name for "controlled by" attribution in the shared room.
function roomBy() {
  try {
    const s = JSON.parse(localStorage.getItem("ssh_settings") || "{}")
    return s.username || s.agent || ""
  } catch {
    return ""
  }
}

const POLL_MS = 3000
const DRIFT_TOLERANCE = 2.5

// ── YouTube IFrame API loader (singleton) ────────────────────────────────────
// The player lives here (app-root provider) — not on the Chill page — so audio
// keeps playing when you navigate away. A persistent fixed host div is rendered
// via portal; the Chill page only tells us which area to overlay it onto.
const YT_API_SRC = "https://www.youtube.com/iframe_api"
let ytApiPromise = null
function loadYT() {
  if (typeof window === "undefined") return Promise.resolve(null)
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT)
  if (ytApiPromise) return ytApiPromise
  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(window.YT) }
    if (!document.querySelector(`script[src="${YT_API_SRC}"]`)) {
      const s = document.createElement("script")
      s.src = YT_API_SRC
      document.head.appendChild(s)
    }
  })
  return ytApiPromise
}

// ── WebAudio noise (white / pink / brown) — no assets needed ─────────────────
function makeNoiseBuffer(ctx, type) {
  const len = ctx.sampleRate * 2
  const buf = ctx.createBuffer(1, len, ctx.sampleRate)
  const d = buf.getChannelData(0)
  let last = 0, b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1
    if (type === "brown") {
      last = (last + 0.02 * w) / 1.02
      d[i] = last * 3.5
    } else if (type === "pink") {
      b0 = 0.99886 * b0 + w * 0.0555179
      b1 = 0.99332 * b1 + w * 0.0750759
      b2 = 0.969 * b2 + w * 0.153852
      b3 = 0.8665 * b3 + w * 0.3104856
      b4 = 0.55 * b4 + w * 0.5329522
      b5 = -0.7616 * b5 - w * 0.016898
      d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11
      b6 = w * 0.115926
    } else {
      d[i] = w
    }
  }
  return buf
}

// ── Persistent YouTube stage ─────────────────────────────────────────────────
// One fixed host <div> holds the iframe and is only ever re-styled (never
// re-parented) so the player keeps playing across route changes AND across
// window↔background mode switches. Two modes:
//   • window     — a lightweight draggable/resizable floating player (default)
//   • background — overlays the Chill content area as an ambient backdrop
// Reusing spotlight's FloatingWindow was rejected: it's bound to the spotlight
// WINDOW_DEFS/context and can't live in this app-root, always-on portal.
const YT_TITLE_H = 30
const YT_MIN_W = 240
const YT_MIN_H = 160

function loadYtWin() {
  try {
    const w = JSON.parse(localStorage.getItem("chill_yt_win"))
    if (w && typeof w.w === "number") return w
  } catch { /* ignore */ }
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280
  const vh = typeof window !== "undefined" ? window.innerHeight : 720
  return { w: 480, h: 300, x: Math.max(12, vw - 500), y: Math.max(12, vh - 340) }
}

const SNAP = 18   // px edge-snap threshold when dropping the window

function ChillYouTubeStage({ hostRef, active, mode, stageRect, title, isPlaying, cover,
  onToggleMode, onClose, onTogglePlay, onNext }) {
  const [win, setWin] = useState(loadYtWin)
  const [minned, setMinned] = useState(false)
  const drag = useRef(null)
  const rz   = useRef(null)

  useEffect(() => { localStorage.setItem("chill_yt_win", JSON.stringify(win)) }, [win])

  const vw = () => (typeof window !== "undefined" ? window.innerWidth : 99999)
  const vh = () => (typeof window !== "undefined" ? window.innerHeight : 99999)
  const clampMove = (x, y, w) => ({
    x: Math.max(0, Math.min(vw() - Math.max(YT_MIN_W, w.w), x)),
    y: Math.max(0, Math.min(vh() - YT_TITLE_H, y)),
  })

  const onDragDown = (e) => {
    if (e.target.closest("button") || e.button !== 0) return
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { sx: e.clientX, sy: e.clientY, ox: win.x, oy: win.y }
  }
  const onDragMove = (e) => {
    const d = drag.current
    if (!d) return
    setWin((w) => ({ ...w, ...clampMove(d.ox + e.clientX - d.sx, d.oy + e.clientY - d.sy, w) }))
  }
  const onDragUp = (e) => {
    if (!drag.current) return
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ }
    drag.current = null
    // Snap to nearby screen edges on drop.
    setWin((w) => {
      let { x, y } = w
      if (x < SNAP) x = 0
      else if (x + w.w > vw() - SNAP) x = vw() - w.w
      if (y < SNAP) y = 0
      else if (y + w.h > vh() - SNAP) y = Math.max(0, vh() - w.h)
      return { ...w, x, y }
    })
  }

  const onRzDown = (e) => {
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    rz.current = { sx: e.clientX, sy: e.clientY, ow: win.w, oh: win.h }
  }
  const onRzMove = (e) => {
    const r = rz.current
    if (!r) return
    setWin((w) => ({
      ...w,
      w: Math.max(YT_MIN_W, Math.min(vw() - w.x, r.ow + e.clientX - r.sx)),
      h: Math.max(YT_MIN_H, r.oh + e.clientY - r.sy),
    }))
  }
  const onRzUp = (e) => { if (rz.current) { try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ } rz.current = null } }

  // Cover-fit: over-scale the iframe so the 16:9 video fills the (non-16:9) host,
  // cropping the letterbox bars. Recomputed on size / cover / active changes.
  useEffect(() => {
    const host = hostRef.current
    const iframe = host?.querySelector("iframe")
    if (!iframe) return
    if (cover && active && mode === "window") {
      const bw = win.w, bh = Math.max(1, win.h - YT_TITLE_H)
      const a = bw / bh, v = 16 / 9
      const s = Math.max(a / v, v / a) * 1.02
      iframe.style.transform = `scale(${s})`
      iframe.style.transformOrigin = "center center"
    } else {
      iframe.style.transform = ""
    }
  }, [cover, active, mode, win.w, win.h, title, hostRef])

  const bodyVisible = active && mode === "window" && !minned
  let hostStyle
  if (active && mode === "background" && stageRect) {
    hostStyle = { position: "fixed", top: stageRect.top, left: stageRect.left, width: stageRect.width,
      height: stageRect.height, zIndex: 1, pointerEvents: "none", overflow: "hidden", border: 0, background: "#000" }
  } else if (bodyVisible) {
    hostStyle = { position: "fixed", top: win.y + YT_TITLE_H, left: win.x, width: win.w, height: win.h - YT_TITLE_H,
      zIndex: 1250, overflow: "hidden", border: 0, background: "#000",
      boxShadow: "0 14px 44px rgba(var(--app-shadow-rgb),0.55)", borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }
  } else {
    hostStyle = { position: "fixed", right: 0, bottom: 0, width: 1, height: 1, opacity: 0,
      pointerEvents: "none", overflow: "hidden", zIndex: -1, border: 0 }
  }

  const btn = { background: "transparent", border: 0, color: "rgba(var(--app-fg-rgb),0.7)", cursor: "pointer",
    fontSize: 13, lineHeight: 1, padding: "2px 4px" }

  return (
    <>
      <div ref={hostRef} aria-hidden={!bodyVisible} style={hostStyle} />
      {active && mode === "window" && (
        <>
          <div
            onPointerDown={onDragDown} onPointerMove={onDragMove} onPointerUp={onDragUp}
            style={{ position: "fixed", top: win.y, left: win.x, width: win.w, height: YT_TITLE_H, zIndex: 1251,
              display: "flex", alignItems: "center", gap: 4, padding: "0 6px", cursor: "move", boxSizing: "border-box",
              background: "rgba(10,10,24,0.94)", borderRadius: minned ? 8 : "8px 8px 0 0",
              borderBottom: minned ? "none" : "1px solid rgba(var(--app-fg-rgb),0.1)", userSelect: "none",
              boxShadow: minned ? "0 8px 24px rgba(var(--app-shadow-rgb),0.5)" : "none",
              font: '600 11px "JetBrains Mono", monospace' }}
          >
            <button onClick={onTogglePlay} title={isPlaying ? "Pause" : "Play"} style={btn}>{isPlaying ? "❚❚" : "▶"}</button>
            <button onClick={onNext} title="Nächster Track" style={btn}>⏭</button>
            <span style={{ flex: 1, minWidth: 0, color: "rgba(var(--app-fg-rgb),0.85)", whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis" }} title={title || "YouTube"}>
              {title || "♪ YOUTUBE"}
            </span>
            <button onClick={() => setMinned((m) => !m)} title={minned ? "Ausklappen" : "Minimieren"} style={btn}>{minned ? "▢" : "—"}</button>
            <button onClick={onToggleMode} title="In den Hintergrund" style={btn}>▦</button>
            <button onClick={onClose} title="Schließen" style={btn}>✕</button>
          </div>
          {bodyVisible && (
            <div
              onPointerDown={onRzDown} onPointerMove={onRzMove} onPointerUp={onRzUp}
              style={{ position: "fixed", top: win.y + win.h - 16, left: win.x + win.w - 16, width: 18, height: 18,
                zIndex: 1252, cursor: "nwse-resize" }}
            />
          )}
        </>
      )}
    </>
  )
}

export function ChillAudioProvider({ children }) {
  const saved = useRef(loadState()).current

  const [config, setConfig]   = useState({ stations: [], ambients: [] })
  const [station, setStation] = useState(null)
  const [isPlaying, setPlaying] = useState(false)
  const [volume, setVolumeState] = useState(saved.volume ?? 0.6)
  const [ambients, setAmbients] = useState(saved.ambients ?? {})   // id -> { on, volume }
  const [noise, setNoiseState]  = useState(saved.noise ?? { type: "off", volume: 0.4 })
  const [loading, setLoading]   = useState(false)

  const musicRef   = useRef(null)
  const ambientRef = useRef({})            // id -> HTMLAudioElement
  const noiseCtx   = useRef(null)
  const noiseSrc   = useRef(null)
  const noiseGain  = useRef(null)

  // ── Global YouTube player ──────────────────────────────────────────────
  const [ytActive, setYtActive]     = useState(false)
  const [ytVideoId, setYtVideoId]   = useState(null)
  const [ytTitle, setYtTitle]       = useState("")
  const [ytIsPlaying, setYtPlaying] = useState(false)
  const [ytVolume, setYtVolumeState] = useState(() => {
    const v = parseFloat(localStorage.getItem("chill_yt_vol"))
    return Number.isFinite(v) ? v : 0.8
  })
  const [ytMode, setYtMode] = useState(() => localStorage.getItem("chill_yt_mode") || "window")
  const toggleYtMode = useCallback(() => setYtMode((m) => {
    const next = m === "window" ? "background" : "window"
    localStorage.setItem("chill_yt_mode", next)
    return next
  }), [])
  const [ytCover, setYtCover] = useState(() => localStorage.getItem("chill_yt_cover") === "1")
  const toggleYtCover = useCallback(() => setYtCover((c) => {
    const next = !c
    localStorage.setItem("chill_yt_cover", next ? "1" : "0")
    return next
  }), [])
  const ytPlayerRef = useRef(null)
  const ytHostRef   = useRef(null)         // portal div; YT.Player mounts inside
  const ytStageEl   = useRef(null)         // page element to overlay the video onto
  const [ytRect, setYtRect] = useState(null)
  const ytVolRef = useRef(ytVolume)

  const reportYt = useCallback((player) => {
    try {
      const d = player?.getVideoData?.()
      if (d?.video_id) setYtVideoId(d.video_id)
      if (typeof d?.title === "string") setYtTitle(d.title)
    } catch { /* not ready */ }
  }, [])

  const playYouTube = useCallback((parsed) => {
    if (!parsed) return
    if (musicRef.current && !musicRef.current.paused) musicRef.current.pause()
    setYtActive(true)
    loadYT().then((YT) => {
      if (!YT || !ytHostRef.current) return
      if (ytPlayerRef.current) { try { ytPlayerRef.current.destroy() } catch { /* ignore */ } }
      const mount = document.createElement("div")
      ytHostRef.current.innerHTML = ""
      ytHostRef.current.appendChild(mount)
      const vars = { autoplay: 1, rel: 0, modestbranding: 1, playsinline: 1 }
      const opts = {
        width: "100%", height: "100%",
        events: {
          onReady: (e) => { try { e.target.setVolume(Math.round(ytVolRef.current * 100)) } catch { /* ignore */ } reportYt(e.target) },
          onStateChange: (e) => { reportYt(e.target); setYtPlaying(e.data === 1) },
        },
      }
      if (parsed.list) opts.playerVars = { ...vars, listType: "playlist", list: parsed.list }
      else { opts.videoId = parsed.video; opts.playerVars = vars }
      ytPlayerRef.current = new YT.Player(mount, opts)
    })
  }, [reportYt])

  const clearYouTube = useCallback(() => {
    if (ytPlayerRef.current) { try { ytPlayerRef.current.destroy() } catch { /* ignore */ } ytPlayerRef.current = null }
    if (ytHostRef.current) ytHostRef.current.innerHTML = ""
    setYtActive(false)
    setYtVideoId(null)
    setYtTitle("")
    setYtPlaying(false)
  }, [])

  const pauseYouTube = useCallback(() => {
    try { ytPlayerRef.current?.pauseVideo?.() } catch { /* ignore */ }
  }, [])

  const ytTogglePlay = useCallback(() => {
    const p = ytPlayerRef.current
    if (!p) return
    try { (p.getPlayerState?.() === 1 ? p.pauseVideo : p.playVideo).call(p) } catch { /* ignore */ }
  }, [])

  const ytNextTrack = useCallback(() => {
    try { ytPlayerRef.current?.nextVideo?.() } catch { /* ignore */ }
  }, [])

  const setYtVolume = useCallback((v) => {
    const vol = Math.max(0, Math.min(1, v))
    ytVolRef.current = vol
    setYtVolumeState(vol)
    localStorage.setItem("chill_yt_vol", String(vol))
    try { ytPlayerRef.current?.setVolume?.(Math.round(vol * 100)) } catch { /* ignore */ }
  }, [])

  // ── Master mute (music + ambients + noise + YouTube) ───────────────────
  const [muted, setMuted] = useState(false)
  const toggleMute = useCallback(() => setMuted((m) => !m), [])

  // Chill page registers the DOM area to overlay the video onto (null = hide).
  const setYouTubeStage = useCallback((el) => { ytStageEl.current = el || null }, [])

  // Track the stage rect so the fixed portal host follows the page layout.
  useEffect(() => {
    let raf = 0
    let last = null
    const tick = () => {
      const el = ytStageEl.current
      let next = null
      if (el) {
        const r = el.getBoundingClientRect()
        next = { top: r.top, left: r.left, width: r.width, height: r.height }
      }
      const changed = !last !== !next ||
        (last && next && (Math.abs(last.top - next.top) > 0.5 || Math.abs(last.left - next.left) > 0.5 ||
          Math.abs(last.width - next.width) > 0.5 || Math.abs(last.height - next.height) > 0.5))
      if (changed) { last = next; setYtRect(next) }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  // Lazily build the persistent music element.
  const music = useMemo(() => {
    if (typeof Audio === "undefined") return null
    const a = new Audio()
    a.preload = "none"
    a.addEventListener("playing", () => setPlaying(true))
    a.addEventListener("pause", () => setPlaying(false))
    a.addEventListener("waiting", () => setLoading(true))
    a.addEventListener("playing", () => setLoading(false))
    musicRef.current = a
    return a
  }, [])

  useEffect(() => { if (music) music.volume = volume }, [music, volume])

  // Apply master mute to every audio source; noise gain restores to its volume.
  useEffect(() => {
    if (musicRef.current) musicRef.current.muted = muted
    Object.values(ambientRef.current).forEach((el) => { if (el) el.muted = muted })
    if (noiseGain.current) noiseGain.current.gain.value = muted ? 0 : noise.volume
    try { (muted ? ytPlayerRef.current?.mute : ytPlayerRef.current?.unMute)?.call(ytPlayerRef.current) } catch { /* ignore */ }
  }, [muted, noise.volume, ambients])

  // Load config once; restore the saved station selection (no autoplay).
  useEffect(() => {
    let alive = true
    fetch("/api/chill/config/")
      .then((r) => r.json())
      .then((cfg) => {
        if (!alive) return
        setConfig(cfg)
        const restore = cfg.stations?.find((s) => s.id === saved.stationId)
        if (restore) setStation(restore)
      })
      .catch(() => {})
    return () => { alive = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist personal state.
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({
      stationId: station?.id ?? null, volume, ambients, noise,
    }))
  }, [station, volume, ambients, noise])

  // ── Music controls ─────────────────────────────────────────────────────
  const playStation = useCallback((st) => {
    if (!music) return
    const url = stationUrl(st)
    if (!url) return
    try { ytPlayerRef.current?.pauseVideo?.() } catch { /* ignore */ }
    if (station?.id !== st.id || music.src !== url) {
      music.src = url
    }
    setStation(st)
    setLoading(true)
    music.play().catch(() => setLoading(false))
  }, [music, station])

  const togglePlay = useCallback(() => {
    if (!music) return
    if (isPlaying) { music.pause(); return }
    if (!music.src && station) { music.src = stationUrl(station) }
    if (!music.src) return
    music.play().catch(() => {})
  }, [music, isPlaying, station])

  const setVolume = useCallback((v) => setVolumeState(Math.max(0, Math.min(1, v))), [])

  // ── Ambient layers ─────────────────────────────────────────────────────
  const ambientDef = useCallback((id) => config.ambients?.find((a) => a.id === id), [config])

  const toggleAmbient = useCallback((id) => {
    setAmbients((prev) => {
      const cur = prev[id] ?? { on: false, volume: 0.5 }
      const next = { ...cur, on: !cur.on }
      const def = ambientDef(id)
      let el = ambientRef.current[id]
      if (next.on && def) {
        if (!el) {
          el = new Audio(AUDIO_BASE + encodeURIComponent(def.file))
          el.loop = true
          ambientRef.current[id] = el
        }
        el.volume = next.volume
        el.play().catch(() => {})
      } else if (el) {
        el.pause()
      }
      return { ...prev, [id]: next }
    })
  }, [ambientDef])

  const setAmbientVolume = useCallback((id, v) => {
    const vol = Math.max(0, Math.min(1, v))
    setAmbients((prev) => {
      const cur = prev[id] ?? { on: false, volume: vol }
      const el = ambientRef.current[id]
      if (el) el.volume = vol
      return { ...prev, [id]: { ...cur, volume: vol } }
    })
  }, [])

  // ── Noise generator ────────────────────────────────────────────────────
  const applyNoise = useCallback((type, vol) => {
    if (type === "off") {
      if (noiseSrc.current) { try { noiseSrc.current.stop() } catch {} noiseSrc.current = null }
      return
    }
    if (typeof window === "undefined" || !window.AudioContext) return
    if (!noiseCtx.current) {
      noiseCtx.current = new AudioContext()
      noiseGain.current = noiseCtx.current.createGain()
      noiseGain.current.connect(noiseCtx.current.destination)
    }
    const ctx = noiseCtx.current
    if (ctx.state === "suspended") ctx.resume()
    noiseGain.current.gain.value = vol
    if (noiseSrc.current) { try { noiseSrc.current.stop() } catch {} }
    const src = ctx.createBufferSource()
    src.buffer = makeNoiseBuffer(ctx, type)
    src.loop = true
    src.connect(noiseGain.current)
    src.start()
    noiseSrc.current = src
  }, [])

  const setNoise = useCallback((type) => {
    setNoiseState((prev) => {
      const next = { ...prev, type }
      applyNoise(type, prev.volume)
      return next
    })
  }, [applyNoise])

  const setNoiseVolume = useCallback((v) => {
    const vol = Math.max(0, Math.min(1, v))
    setNoiseState((prev) => {
      if (noiseGain.current && !muted) noiseGain.current.gain.value = vol
      return { ...prev, volume: vol }
    })
  }, [muted])

  // ── Mixer presets ──────────────────────────────────────────────────────
  // Apply a saved {noise, ambients} combo in one shot (used by the preset chips).
  const applyMix = useCallback((mix) => {
    if (!mix) return
    if (mix.noise) { setNoiseState((p) => ({ ...p, ...mix.noise })); applyNoise(mix.noise.type ?? "off", mix.noise.volume ?? 0.4) }
    if (mix.ambients) {
      setAmbients(() => {
        const nextState = {}
        for (const a of config.ambients ?? []) {
          const want = mix.ambients[a.id]
          const on = !!want?.on
          const vol = want?.volume ?? 0.5
          nextState[a.id] = { on, volume: vol }
          let el = ambientRef.current[a.id]
          if (on) {
            if (!el) { el = new Audio(AUDIO_BASE + encodeURIComponent(a.file)); el.loop = true; ambientRef.current[a.id] = el }
            el.volume = vol; el.muted = muted; el.play().catch(() => {})
          } else if (el) { el.pause() }
        }
        return nextState
      })
    }
  }, [applyNoise, config, muted])

  const value = {
    config, station, isPlaying, volume, ambients, noise, loading,
    playStation, togglePlay, setVolume,
    toggleAmbient, setAmbientVolume,
    setNoise, setNoiseVolume, applyMix,
    hasStation: !!station,
    muted, toggleMute,
    ytActive, ytVideoId, ytTitle, ytIsPlaying, ytVolume, ytMode, ytCover,
    playYouTube, clearYouTube, pauseYouTube, toggleYtMode, toggleYtCover,
    ytTogglePlay, ytNextTrack, setYtVolume, setYouTubeStage,
  }

  return (
    <ChillCtx.Provider value={value}>
      {children}
      {typeof document !== "undefined" && createPortal(
        <ChillYouTubeStage
          hostRef={ytHostRef}
          active={ytActive}
          mode={ytMode}
          stageRect={ytRect}
          title={ytTitle}
          isPlaying={ytIsPlaying}
          cover={ytCover}
          onToggleMode={toggleYtMode}
          onClose={clearYouTube}
          onTogglePlay={ytTogglePlay}
          onNext={ytNextTrack}
        />,
        document.body,
      )}
    </ChillCtx.Provider>
  )
}
